import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowTopRightOnSquareIcon, ArrowUpTrayIcon, CheckCircleIcon,
  ExclamationTriangleIcon, InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { openProSearch } from '../../utils/proSearch';
import {
  applyFile, CHUNK_SIZE, extractDetail, getReferenceData, validateFile,
  VERDICT_LABELS,
  type ApplyResponse, type ContainerPreview, type FileError,
  type ThdOtwReferenceData, type ValidateResponse, type Verdict,
} from '../../services/thdOtwImport.service';

/**
 * Home Depot OTW Upload — 360 port of v1 cmshomedepot/getotw_csv.pl.
 *
 * Upload the OTW file, read what the run will do, then run it. The unit
 * throughout is the CONTAINER, not the file row: several rows per container is
 * normal (a container carries multiple BOLs, POs and departments), and one
 * container becomes one pro.
 *
 * There are no per-row checkboxes. An OTW run always creates what is new and
 * always updates what has changed — the preview exists to be read, not chosen
 * from. Any validation error rejects the whole file; nothing is written
 * partially.
 */

const VERDICT_ORDER: Verdict[] = ['create', 'update', 'no_change', 'old_move', 'deleted', 'non_gwi'];

const VERDICT_STYLES: Record<Verdict, string> = {
  create: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  no_change: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  old_move: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  deleted: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  non_gwi: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

const VerdictPill: React.FC<{ v: Verdict }> = ({ v }) => (
  <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${VERDICT_STYLES[v]}`}>
    {VERDICT_LABELS[v]}
  </span>
);

// One entry per CAUSE. A bad mapping errors on every row that uses it, so the
// count plus a few sample lines says far more than one line per row: 200
// identical lines for seven broken mappings is not a readable error list.
const ErrorList: React.FC<{ errors: FileError[] }> = ({ errors }) => {
  const affected = errors.reduce((n, e) => n + (e.count || 0), 0);
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/30">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-800 dark:text-red-300">
        <ExclamationTriangleIcon className="h-5 w-5" />
        Nothing was imported — {errors.length} problem{errors.length === 1 ? '' : 's'} to fix
        {affected > 0 && `, affecting ${affected} row${affected === 1 ? '' : 's'}`}.
      </div>
      <ul className="space-y-2 text-sm text-red-800 dark:text-red-300">
        {errors.map((e, i) => (
          <li key={i} className="leading-snug">
            <span>{e.message}</span>
            {e.count > 1 && (
              <span className="ml-1.5 whitespace-nowrap rounded-full bg-red-200 px-1.5 py-0.5 text-xs font-semibold dark:bg-red-800">
                {e.count} rows
              </span>
            )}
            {e.fix_hint && <div className="mt-0.5 italic opacity-80">{e.fix_hint}</div>}
            {e.rows.length > 0 && (
              <div className="mt-0.5 font-mono text-xs opacity-70">
                line{e.rows.length === 1 ? '' : 's'} {e.rows.join(', ')}
                {e.count > e.rows.length && ' …'}
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-red-700 dark:text-red-400">
        Most of these are configuration rather than bad data. Check{' '}
        <Link to="/data-management/home-depot-template-mapping" className="underline">
          Home Depot Template Mapping
        </Link>
        {' '}— a mapping whose template has been deleted does not count as mapped.
      </p>
    </div>
  );
};

const ThdOtwUploadPage: React.FC = () => {
  const [ref, setRef] = useState<ThdOtwReferenceData | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<'validate' | 'apply' | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<ValidateResponse | null>(null);
  const [result, setResult] = useState<ApplyResponse | null>(null);
  const [filter, setFilter] = useState<Verdict | 'all'>('all');
  const [drawerPro, setDrawerPro] = useState<number | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    getReferenceData().then(setRef).catch(() => setRef(null));
  }, []);

  const onPickFile = (f: File | null) => {
    setFile(f);
    setPreview(null);
    setResult(null);
    setFilter('all');
    setErrorMsg(null);
    setProgress(null);
  };

  const onValidate = async () => {
    if (!file) return;
    setErrorMsg(null);
    setResult(null);
    setBusy('validate');
    try {
      setPreview(await validateFile(file));
    } catch (e) {
      setPreview(null);
      setErrorMsg(extractDetail(e));
    } finally {
      setBusy(null);
    }
  };

  // Chunked: a create costs 96 SQL statements, so a 200-container file is minutes
  // of work. One request per chunk keeps every request short whatever the latency,
  // and gives the user a progress count instead of a spinner that looks hung —
  // which is exactly how the first full run read.
  const onApply = async () => {
    if (!file || !preview) return;
    setErrorMsg(null);
    setBusy('apply');

    const toWrite = preview.containers
      .filter((c) => c.verdict === 'create' || c.verdict === 'update')
      .map((c) => c.container);
    const chunks: string[][] = [];
    for (let i = 0; i < toWrite.length; i += CHUNK_SIZE) {
      chunks.push(toWrite.slice(i, i + CHUNK_SIZE));
    }

    setProgress({ done: 0, total: toWrite.length });
    const merged: ApplyResponse = {
      ok: true, filename: file.name, batch_id: '', created: 0, updated: 0,
      skipped: 0, failed: 0, results: [], pronumbers: [], errors: [],
    };
    try {
      for (let i = 0; i < chunks.length; i += 1) {
        const res = await applyFile(file, {
          containers: chunks[i],
          batchId: merged.batch_id || undefined,
          final: i === chunks.length - 1,
        });
        if (!res.ok && res.errors.length) {
          setResult(res);
          setPreview(null);
          return;
        }
        merged.batch_id = merged.batch_id || res.batch_id;
        merged.created += res.created;
        merged.updated += res.updated;
        merged.failed += res.failed;
        // No client-side filtering: the server processed exactly this chunk, so
        // what it returns IS this chunk's result. Second-guessing it here only
        // drops rows when the two disagree.
        merged.results.push(...res.results);
        merged.pronumbers.push(...res.pronumbers);
        merged.ok = merged.ok && res.ok;
        setProgress({ done: Math.min((i + 1) * CHUNK_SIZE, toWrite.length), total: toWrite.length });
        // Show what has landed so far, so a long run is visibly progressing.
        setResult({ ...merged });
      }
      merged.skipped = preview.containers.length - toWrite.length;
      setResult({ ...merged });
    } catch (e) {
      setErrorMsg(extractDetail(e));
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const willWrite = useMemo(
    () => (preview ? preview.counts.create + preview.counts.update : 0),
    [preview],
  );

  const shown = useMemo(() => {
    if (!preview) return [];
    return filter === 'all'
      ? preview.containers
      : preview.containers.filter((c) => c.verdict === filter);
  }, [preview, filter]);

  const openAllPros = (pros: string[]) => {
    if (pros.length) openProSearch(pros);
  };

  return (
    <div className="mx-auto max-w-[1600px] p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-heading">Home Depot OTW Upload</h1>
        <p className="mt-1 text-sm text-muted">
          Upload the OTW file to create new orders and bring existing ones up to date.
          Each container becomes one pro — several file rows for the same container are
          combined.
        </p>
      </header>

      {/* ── file picker ─────────────────────────────────────────────── */}
      <section className="mb-6 rounded-lg border border-rule bg-panel p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-rule px-4 py-2 text-sm font-medium text-body hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            Choose file
          </button>
          <span className="text-sm text-muted">
            {file ? file.name : 'No file chosen'}
          </span>
          <div className="ml-auto flex gap-3">
            <button
              onClick={onValidate}
              disabled={!file || busy !== null}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-50"
            >
              {busy === 'validate' ? 'Checking…' : 'Review'}
            </button>
            <button
              onClick={onApply}
              disabled={!preview?.ok || willWrite === 0 || busy !== null}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {busy === 'apply'
                ? (progress ? `Running… ${progress.done}/${progress.total}` : 'Running…')
                : `Run import${willWrite ? ` (${willWrite})` : ''}`}
            </button>
          </div>
        </div>
        {ref && (
          <p className="mt-3 text-xs text-muted">
            .csv, .xlsx or .xls. {ref.columns.length} columns in any order, all required
            to be present; {ref.required_columns.length} must carry a value:{' '}
            <span className="font-mono">{ref.required_columns.join(', ')}</span>.
          </p>
        )}
      </section>

      {errorMsg && (
        <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
          {errorMsg}
        </div>
      )}

      {preview && !preview.ok && preview.errors.length > 0 && (
        <div className="mb-6"><ErrorList errors={preview.errors} /></div>
      )}
      {result && !result.ok && result.errors.length > 0 && (
        <div className="mb-6"><ErrorList errors={result.errors} /></div>
      )}

      {/* ── results of a run ────────────────────────────────────────── */}
      {result && result.ok && (
        <section className="mb-6 rounded-lg border border-green-300 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/30">
          <div className="flex items-center gap-2 text-sm font-semibold text-green-800 dark:text-green-300">
            <CheckCircleIcon className="h-5 w-5" />
            Imported {result.filename} — {result.created} created, {result.updated} updated,
            {' '}{result.skipped} unchanged
            {result.failed > 0 && `, ${result.failed} failed`}.
          </div>
          {result.pronumbers.length > 0 && (
            <button
              onClick={() => openAllPros(result.pronumbers)}
              className="mt-2 inline-flex items-center gap-1 text-sm text-accent-text underline"
            >
              Open all {result.pronumbers.length} in Search Results
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {result.results.filter((r) => r.pronumber && r.ok).map((r) => (
              <button
                key={r.container}
                onClick={() => setDrawerPro(Number(r.pronumber))}
                className="rounded border border-green-300 bg-white px-2 py-1 font-mono text-xs text-accent-text hover:bg-green-100 dark:border-green-700 dark:bg-gray-800"
                title={`${r.container} — ${r.action}`}
              >
                {r.pronumber}
              </button>
            ))}
          </div>
          {result.failed > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-red-800 dark:text-red-300">
              {result.results.filter((r) => !r.ok).map((r) => (
                <li key={r.container}>
                  <span className="font-mono">{r.container}</span>: {r.error}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── the plan ────────────────────────────────────────────────── */}
      {preview?.ok && (
        <section className="rounded-lg border border-rule bg-panel">
          <div className="flex flex-wrap items-center gap-2 border-b border-rule p-4">
            <button
              onClick={() => setFilter('all')}
              className={`rounded-full px-3 py-1 text-xs font-medium ${filter === 'all' ? 'bg-accent text-accent-fg' : 'bg-gray-100 text-body dark:bg-gray-700'}`}
            >
              All {preview.containers.length}
            </button>
            {VERDICT_ORDER.map((v) => {
              const n = preview.counts[v] ?? 0;
              if (!n) return null;
              return (
                <button
                  key={v}
                  onClick={() => setFilter(v)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${filter === v ? 'bg-accent text-accent-fg' : VERDICT_STYLES[v]}`}
                >
                  {VERDICT_LABELS[v]} {n}
                </button>
              );
            })}
            <span className="ml-auto text-xs text-muted">
              {preview.total_rows} row{preview.total_rows === 1 ? '' : 's'} →{' '}
              {preview.containers.length} container{preview.containers.length === 1 ? '' : 's'}
            </span>
          </div>

          {(preview.eta_warnings > 0 || preview.legacy_divergences > 0) && (
            <div className="border-b border-rule px-4 py-2 text-xs">
              {preview.eta_warnings > 0 && (
                <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  {preview.eta_warnings} container{preview.eta_warnings === 1 ? ' has an ETA' : 's have ETAs'}
                  {' '}more than 60 days out. They are still imported.
                </div>
              )}
              {preview.legacy_divergences > 0 && (
                <div className="mt-1 flex items-center gap-1.5 text-blue-700 dark:text-blue-400">
                  <InformationCircleIcon className="h-4 w-4" />
                  Legacy CMS could have decided {preview.legacy_divergences} container
                  {preview.legacy_divergences === 1 ? '' : 's'} differently — marked below.
                  Useful when comparing a run side by side.
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-rule bg-gray-50 text-xs uppercase text-muted dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left">Container</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-left">IDC</th>
                  <th className="px-3 py-2 text-left">SCAC</th>
                  <th className="px-3 py-2 text-left">Mode</th>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-left">BOL</th>
                  <th className="px-3 py-2 text-left">PO</th>
                  <th className="px-3 py-2 text-left">ETA</th>
                  <th className="px-3 py-2 text-left">DC Due</th>
                  <th className="px-3 py-2 text-left">Detail</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <ContainerRow key={c.container} c={c} onOpenPro={setDrawerPro} />
                ))}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-3 py-6 text-center text-muted">
                      No containers with that verdict.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={() => setDrawerPro(null)}
      />
    </div>
  );
};

const ContainerRow: React.FC<{
  c: ContainerPreview;
  onOpenPro: (pro: number) => void;
}> = ({ c, onOpenPro }) => (
  <tr className="border-b border-rule last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50">
    <td className="px-3 py-2 font-mono">
      {c.container}
      <div className="text-[0.65rem] text-muted">
        line{c.rows.length === 1 ? '' : 's'} {c.rows.join(', ')}
      </div>
    </td>
    <td className="px-3 py-2"><VerdictPill v={c.verdict} /></td>
    <td className="px-3 py-2">{c.dcnumber}</td>
    <td className="px-3 py-2">{c.scac}</td>
    <td className="px-3 py-2">
      {c.mode}
      {/* A CCVT move is looked up under CCVT and stored as AWR — show both. */}
      {c.raw_mode !== c.mode && (
        <span className="ml-1 text-[0.65rem] text-muted">(file: {c.raw_mode})</span>
      )}
    </td>
    <td className="px-3 py-2 font-mono text-xs">{c.template}</td>
    <td className="px-3 py-2 text-xs">{c.bol}</td>
    <td className="px-3 py-2 text-xs">{c.po}</td>
    <td className="px-3 py-2 text-xs">
      {c.etadate}
      {c.eta_warning && (
        <span className="ml-1 text-amber-600 dark:text-amber-400" title="More than 60 days out">
          !
        </span>
      )}
    </td>
    <td className="px-3 py-2 text-xs">{c.dcduedate}</td>
    <td className="px-3 py-2 text-xs">
      <div className="text-muted">{c.reason}</div>
      {c.pronumbers.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {c.pronumbers.map((p) => (
            <button
              key={p}
              onClick={() => onOpenPro(Number(p))}
              className="font-mono text-accent-text underline"
            >
              {p}
            </button>
          ))}
        </div>
      )}
      {c.changes.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {c.changes.map((ch) => (
            <li key={ch.column} className="font-mono text-[0.65rem]">
              <span className="text-muted">{ch.column}:</span>{' '}
              <span className="line-through opacity-60">{ch.old || '—'}</span>{' '}
              → <span className="text-body">{ch.new}</span>
            </li>
          ))}
        </ul>
      )}
      {c.triaxle === 'Y' && (
        <div className="mt-0.5 text-[0.65rem] text-amber-700 dark:text-amber-400">
          Overweight — triaxle, swing, no leg drop
        </div>
      )}
      {c.conflicts.length > 0 && (
        <div className="mt-0.5 text-[0.65rem] text-amber-700 dark:text-amber-400">
          Rows disagree on {c.conflicts.join(', ')} — the last one wins
        </div>
      )}
      {c.legacy_divergence && (
        <div className="mt-0.5 text-[0.65rem] text-blue-700 dark:text-blue-400">
          {c.legacy_divergence}
        </div>
      )}
    </td>
  </tr>
);

export default ThdOtwUploadPage;
